import { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  selectTherapistProfile,
  selectTherapistId,
} from "../../store/slices/authSlice";
import { useUpdateTherapistMutation } from "../../store/services/therapistApi";
import {
  Rocket,
  Sparkle,
  Star,
  UsersThree,
  CalendarCheck,
  Toolbox,
  CurrencyCircleDollar,
  Gear,
  Buildings,
  House,
  Question,
  Headset,
  CaretLeft,
  ArrowLeft,
  CheckCircle,
} from "@phosphor-icons/react";
import lennyIcon from "../../assets/lenny-icon-512.jpg";
import "./WelcomeOnboarding.css";

const COMPLETED_KEY = "clinikit_welcome_onboarding_done";

const SPOTLIGHT_PAD = 10;
const CARD_GAP = 18;
const MOBILE_BP = 1024;

// Step "help" spotlights BOTH support channels: the purple "?" button (primary,
// drives card placement) and Lenny's chat FAB (.lenny-fab, pinned to the left edge).
const DESKTOP_STEPS = [
  { id: "welcome", targets: null },
  { id: "profile", targets: [".user-menu-container"], placement: "below" },
  { id: "sidebar", targets: [".sidebar-nav"], placement: "left" },
  { id: "help", targets: [".help-button-wrapper", ".lenny-fab"], placement: "below" },
];

const MOBILE_STEPS = [
  { id: "welcome", targets: null },
  { id: "profile", targets: [".user-menu-container"], placement: "below" },
  { id: "sidebar", targets: [".mobile-menu-btn"], placement: "below" },
  { id: "help", targets: [".help-button-wrapper", ".lenny-fab"], placement: "above" },
];

const TOUR_ACTIVE_CLASS = "wo-tour-active";

function WelcomeOnboarding() {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [rects, setRects] = useState([]);
  const [cardStyle, setCardStyle] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BP);
  const cardRef = useRef(null);

  const therapistProfile = useSelector(selectTherapistProfile);
  const therapistId = useSelector(selectTherapistId);
  const [updateTherapist] = useUpdateTherapistMutation();

  const STEPS = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;

  // Mobile detection
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BP);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Decide whether to show on mount (new users)
  useEffect(() => {
    if (!therapistProfile || !therapistId) return;
    if (therapistProfile.onboardingCompleted === true) return;
    if (therapistProfile.helpWelcomeSeen === true) return;
    // Multi-clinic v2: an invited member first sees the team welcome popup;
    // the tour starts once the invite is marked accepted (profile snapshot).
    if (therapistProfile.inviteStatus === "pending") return;
    if (localStorage.getItem(`${COMPLETED_KEY}_${therapistId}`) === "true") return;

    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, [therapistProfile, therapistId]);

  // Listen for manual re-trigger via custom event
  useEffect(() => {
    const handleTrigger = () => {
      setStep(0);
      setRects([]);
      setCardStyle({});
      setVisible(true);
    };
    window.addEventListener("clinikit:start-onboarding", handleTrigger);
    return () => window.removeEventListener("clinikit:start-onboarding", handleTrigger);
  }, []);

  // Measure target elements for current step
  const measureTargets = useCallback(() => {
    const cfg = STEPS[step];
    if (!cfg?.targets) {
      setRects([]);
      return;
    }
    const measured = cfg.targets
      .map((sel) => {
        const el = document.querySelector(sel);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return null;
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      })
      .filter(Boolean);
    setRects(measured);
  }, [step, STEPS]);

  // Body class while the tour is open — lets CSS pull Lenny's FAB (normally
  // z-index ~1e9) between the overlay and the card so the spotlight ring reads
  // correctly and the FAB can't cover the card.
  useEffect(() => {
    if (!visible) return;
    document.body.classList.add(TOUR_ACTIVE_CLASS);
    return () => document.body.classList.remove(TOUR_ACTIVE_CLASS);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    measureTargets();
    window.addEventListener("resize", measureTargets);
    window.addEventListener("scroll", measureTargets, true);
    return () => {
      window.removeEventListener("resize", measureTargets);
      window.removeEventListener("scroll", measureTargets, true);
    };
  }, [visible, measureTargets]);

  // Position the card relative to spotlight
  useLayoutEffect(() => {
    if (!visible) { setCardStyle({}); return; }
    const cfg = STEPS[step];
    if (!cfg?.targets || rects.length === 0) { setCardStyle({}); return; }
    if (!cardRef.current) return;

    const cardRect = cardRef.current.getBoundingClientRect();
    const cw = cardRect.width;
    const ch = cardRect.height;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const primary = rects[0];
    const secondary = rects.slice(1);

    // Card must never cover a secondary spotlight target (e.g. Lenny's FAB on
    // the left edge). If the card box intersects a secondary cutout, push the
    // card's left edge past that cutout.
    const clearOf = (box) => {
      let left = box.left;
      secondary.forEach((r) => {
        const cutL = r.left - SPOTLIGHT_PAD;
        const cutR = r.left + r.width + SPOTLIGHT_PAD;
        const cutT = r.top - SPOTLIGHT_PAD;
        const cutB = r.top + r.height + SPOTLIGHT_PAD;
        const intersects =
          left < cutR &&
          left + box.width > cutL &&
          box.top < cutB &&
          box.top + box.height > cutT;
        if (intersects) left = Math.max(left, cutR + CARD_GAP);
      });
      return left;
    };

    if (isMobile) {
      const spotBottom = primary.top + primary.height + SPOTLIGHT_PAD + CARD_GAP;
      const fitsBelow = spotBottom + ch + 24 <= vh;
      const spotTop = primary.top - SPOTLIGHT_PAD - CARD_GAP;
      const fitsAbove = spotTop - ch >= 12;

      let style;
      let cardTop;
      if (cfg.placement === "above" && fitsAbove) {
        style = { bottom: vh - spotTop };
        cardTop = spotTop - ch;
      } else if (fitsBelow) {
        style = { top: spotBottom };
        cardTop = spotBottom;
      } else {
        style = { bottom: 8 };
        cardTop = vh - 8 - ch;
      }
      // Sheet spans the full width; shrink it from the left if it would cover
      // a secondary target.
      const shiftedLeft = clearOf({ left: 0, top: cardTop, width: vw, height: ch });
      if (shiftedLeft > 0) {
        style = { ...style, left: shiftedLeft, right: 0 };
      }
      setCardStyle(style);
      return;
    }

    let top, left;
    if (cfg.placement === "below") {
      top = primary.top + primary.height + SPOTLIGHT_PAD + CARD_GAP;
      left = primary.left + primary.width / 2 - cw / 2;
    } else if (cfg.placement === "left") {
      top = primary.top + primary.height / 2 - ch / 2;
      left = primary.left - SPOTLIGHT_PAD - CARD_GAP - cw;
    } else if (cfg.placement === "above") {
      top = primary.top - SPOTLIGHT_PAD - CARD_GAP - ch;
      left = primary.left + primary.width / 2 - cw / 2;
    } else {
      top = primary.top + primary.height + SPOTLIGHT_PAD + CARD_GAP;
      left = primary.left;
    }

    top = Math.max(12, Math.min(vh - ch - 12, top));
    left = Math.max(12, Math.min(vw - cw - 12, left));
    left = clearOf({ left, top, width: cw, height: ch });
    left = Math.min(vw - cw - 12, left);
    setCardStyle({ top, left });
  }, [visible, step, rects, isMobile, STEPS]);

  // Complete / skip
  const finish = useCallback(
    (completed) => {
      setVisible(false);
      if (!therapistId) return;
      localStorage.setItem(`${COMPLETED_KEY}_${therapistId}`, "true");
      updateTherapist({
        uid: therapistId,
        data: { onboardingCompleted: true, helpWelcomeSeen: true },
      }).catch(() => {});
    },
    [therapistId, updateTherapist]
  );

  const handleNext = useCallback(() => {
    if (step >= STEPS.length - 1) {
      finish(true);
    } else {
      setStep((s) => s + 1);
    }
  }, [step, finish]);

  const handleSkip = useCallback(() => finish(false), [finish]);

  // Keyboard
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleSkip();
      if (e.key === "ArrowLeft" || e.key === "Enter") handleNext();
      if (e.key === "ArrowRight" && step > 0) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, handleSkip, handleNext, step]);

  if (!visible) return null;

  const firstName = therapistProfile?.firstName || "";
  const isWelcome = step === 0;
  const isLast = step === STEPS.length - 1;
  const hasSpotlight = rects.length > 0 && !isWelcome;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Build SVG cutout rects
  const cutouts = rects.map((r) => ({
    x: r.left - SPOTLIGHT_PAD,
    y: r.top - SPOTLIGHT_PAD,
    w: r.width + SPOTLIGHT_PAD * 2,
    h: r.height + SPOTLIGHT_PAD * 2,
  }));

  // Card classes
  const isCentered = isWelcome || !hasSpotlight;
  const cardCls = [
    "wo-card",
    isCentered ? "wo-centered" : "",
    isMobile && hasSpotlight ? "wo-mobile-sheet" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const hasPos = !isCentered && (cardStyle.top != null || cardStyle.bottom != null);
  const inlineCardStyle = hasPos ? { ...cardStyle } : {};

  return createPortal(
    <>
      {/* Skip button (steps 1-3, not on welcome) */}
      {!isWelcome && (
        <button className="wo-skip-btn" onClick={handleSkip}>
          דלג על ההדרכה
        </button>
      )}

      {/* SVG overlay with cutouts */}
      <svg
        className="wo-overlay-svg"
        viewBox={`0 0 ${vw} ${vh}`}
        preserveAspectRatio="none"
      >
        <defs>
          <mask id="wo-mask">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {cutouts.map((c, i) => (
              <rect
                key={i}
                x={c.x}
                y={c.y}
                width={c.w}
                height={c.h}
                rx="12"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width={vw}
          height={vh}
          fill="rgba(0,0,0,0.52)"
          mask="url(#wo-mask)"
        />
        {cutouts.map((c, i) => (
          <rect
            key={`ring-${i}`}
            className="wo-ring-rect"
            x={c.x - 3}
            y={c.y - 3}
            width={c.w + 6}
            height={c.h + 6}
            rx="14"
          />
        ))}
      </svg>

      {/* Content Card */}
      <div
        ref={cardRef}
        className={cardCls}
        key={`${step}-${isMobile ? "m" : "d"}`}
        style={inlineCardStyle}
      >
        {isWelcome
          ? renderWelcome(firstName, handleNext, handleSkip)
          : renderStep(step, isLast, handleNext, handleSkip, isMobile)}
      </div>
    </>,
    document.body
  );
}

/* =======================================================
   Step 0 — Welcome
   ======================================================= */
function renderWelcome(firstName, onNext, onSkip) {
  return (
    <>
      <div className="wo-welcome-hero">
        <div className="wo-welcome-icon-wrap">
          <Rocket size={38} weight="duotone" />
          <div className="wo-particles">
            <Sparkle size={16} weight="fill" className="wo-particle" />
            <Star size={14} weight="fill" className="wo-particle" />
            <Sparkle size={13} weight="fill" className="wo-particle" />
            <Star size={15} weight="fill" className="wo-particle" />
          </div>
        </div>

        <h2 className="wo-welcome-greeting">
          היי {firstName}!
        </h2>
        <h3 className="wo-welcome-subtitle">ברוכים הבאים ל-CliniKit</h3>
        <p className="wo-welcome-text">
          שמחים שהצטרפת! המערכת נבנתה כדי לעזור לך לנהל את הקליניקה בצורה חכמה
          ופשוטה.
          <br />
          הנה טעימה ממה שמחכה לך:
        </p>
      </div>

      <div className="wo-card-body">
        <div className="wo-features">
          <div className="wo-feature-item">
            <div className="wo-feature-icon fi-patients">
              <UsersThree size={19} weight="duotone" />
            </div>
            <div className="wo-feature-text">
              <span className="wo-feature-title">ניהול מטופלים</span>
              <span className="wo-feature-desc">
                כל המידע, ההיסטוריה והקבצים במקום אחד
              </span>
            </div>
          </div>

          <div className="wo-feature-item">
            <div className="wo-feature-icon fi-calendar">
              <CalendarCheck size={19} weight="duotone" />
            </div>
            <div className="wo-feature-text">
              <span className="wo-feature-title">יומן ואישורי הגעה</span>
              <span className="wo-feature-desc">
                ניהול פגישות ושליחת תזכורות אוטומטיות
              </span>
            </div>
          </div>

          <div className="wo-feature-item">
            <div className="wo-feature-icon fi-toolbox">
              <Toolbox size={19} weight="duotone" />
            </div>
            <div className="wo-feature-text">
              <span className="wo-feature-title">ארגז כלים</span>
              <span className="wo-feature-desc">
                טפסים, שאלונים ותבניות לדו&quot;חות סיכום טיפול — הכל מותאם אישית
              </span>
            </div>
          </div>

          <div className="wo-feature-item">
            <div className="wo-feature-icon fi-finance">
              <CurrencyCircleDollar size={19} weight="duotone" />
            </div>
            <div className="wo-feature-text">
              <span className="wo-feature-title">מעקב פיננסי</span>
              <span className="wo-feature-desc">
                תשלומים, חשבוניות ומעקב הכנסות הקליניקה
              </span>
            </div>
          </div>
        </div>

        <p className="wo-more-text">ועוד הרבה יכולות שמחכות לך!</p>
      </div>

      <div className="wo-welcome-footer">
        <button className="wo-btn-start" onClick={onNext}>
          בואו נכיר את המערכת
          <ArrowLeft size={18} weight="bold" />
        </button>
        <span className="wo-steps-hint">3 שלבים קצרים וסיימנו</span>
        <button className="wo-welcome-skip" onClick={onSkip}>
          אני מכיר/ה, תודה — דלג
        </button>
      </div>
    </>
  );
}

/* =======================================================
   Steps 1-3 — With highlight & footer nav
   ======================================================= */
function renderStep(stepIdx, isLast, onNext, onSkip, mobile) {
  const content = STEP_CONTENT[stepIdx];
  if (!content) return null;

  const title = mobile && content.mobileTitle ? content.mobileTitle : content.title;
  const subtitle = mobile && content.mobileSubtitle != null ? content.mobileSubtitle : content.subtitle;
  const body = mobile && content.mobileBody ? content.mobileBody : content.body;

  return (
    <>
      <div className="wo-card-header">
        <div className="wo-card-icon">{content.icon}</div>
        <div>
          <h3 className="wo-card-title">{title}</h3>
          {subtitle && (
            <p className="wo-card-subtitle">{subtitle}</p>
          )}
        </div>
      </div>

      <div className="wo-card-body">{body}</div>

      <div className="wo-card-footer">
        <button className="wo-btn wo-btn-primary" onClick={onNext}>
          {isLast ? "סיימתי — בואו נתחיל!" : "הבא"}
          <CaretLeft size={16} weight="bold" />
        </button>

        <div className="wo-progress">
          {[1, 2, 3].map((d) => (
            <span
              key={d}
              className={`wo-dot${
                d === stepIdx ? " active" : d < stepIdx ? " done" : ""
              }`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

/* =======================================================
   Step Content Definitions
   ======================================================= */
const STEP_CONTENT = {
  // Step 1 — Profile & Settings
  1: {
    icon: <Gear size={24} weight="duotone" />,
    title: "הפרופיל וההגדרות",
    subtitle: "לחיצה על האזור המסומן תפתח תפריט עם הגדרות",
    body: (
      <>
        <p>
          זה האזור שלך! בלחיצה עליו תמצא/י את כל מה שצריך כדי להתאים אישית
          את החוויה:
        </p>

        <div className="wo-settings-list">
          <div className="wo-settings-item">
            <div className="wo-settings-icon">
              <Gear size={20} weight="duotone" />
            </div>
            <div className="wo-settings-text">
              <span className="wo-settings-title">הגדרות מטפל</span>
              <span className="wo-settings-desc">
                פרטים אישיים, תעריף ברירת מחדל, זמן פגישה, חתימה דיגיטלית
                והעדפות נוספות
              </span>
            </div>
          </div>

          <div className="wo-settings-item">
            <div className="wo-settings-icon">
              <Buildings size={20} weight="duotone" />
            </div>
            <div className="wo-settings-text">
              <span className="wo-settings-title">הגדרות קליניקה</span>
              <span className="wo-settings-desc">
                שם הקליניקה, לוגו, הגדרות SMS, חדרים ומטפלים נוספים
              </span>
            </div>
          </div>
        </div>

        <p style={{ fontSize: "0.82rem", color: "var(--color-text-light)" }}>
          ומכאן גם אפשר להתנתק מהמערכת.
        </p>
      </>
    ),
  },

  // Step 2 — Sidebar Navigation
  2: {
    icon: <House size={24} weight="duotone" />,
    title: "תפריט הניווט",
    subtitle: "כל הקסם קורה כאן!",
    mobileSubtitle: "לחיצה על הכפתור המסומן תפתח את התפריט",
    body: (
      <>
        <p>
          דרך התפריט הצדדי תוכל/י לנווט בין כל חלקי המערכת:
        </p>

        <div className="wo-menu-list">
          <div className="wo-menu-item">
            <span className="wo-menu-item-icon">
              <House size={18} weight="duotone" />
            </span>
            <span className="wo-menu-item-label">דף הבית</span>
            <span className="wo-menu-item-dash">—</span>
            <span className="wo-menu-item-desc">
              סיכום יומי, פגישות ממתינות ומשימות
            </span>
          </div>

          <div className="wo-menu-item">
            <span className="wo-menu-item-icon">
              <UsersThree size={18} weight="duotone" />
            </span>
            <span className="wo-menu-item-label">המטופלים שלי</span>
            <span className="wo-menu-item-dash">—</span>
            <span className="wo-menu-item-desc">
              רשימת מטופלים וכרטיסי לקוח
            </span>
          </div>

          <div className="wo-menu-item">
            <span className="wo-menu-item-icon">
              <CalendarCheck size={18} weight="duotone" />
            </span>
            <span className="wo-menu-item-label">יומן ותורים</span>
            <span className="wo-menu-item-dash">—</span>
            <span className="wo-menu-item-desc">
              ניהול פגישות ואישורי הגעה
            </span>
          </div>

          <div className="wo-menu-item">
            <span className="wo-menu-item-icon">
              <Toolbox size={18} weight="duotone" />
            </span>
            <span className="wo-menu-item-label">ארגז הכלים</span>
            <span className="wo-menu-item-dash">—</span>
            <span className="wo-menu-item-desc">
              טפסים, שאלונים, תבניות ומשימות
            </span>
          </div>

          <div className="wo-menu-item">
            <span className="wo-menu-item-icon">
              <CurrencyCircleDollar size={18} weight="duotone" />
            </span>
            <span className="wo-menu-item-label">פיננסים ותשלומים</span>
            <span className="wo-menu-item-dash">—</span>
            <span className="wo-menu-item-desc">
              מעקב תשלומים, חשבוניות ודוחות
            </span>
          </div>
        </div>
      </>
    ),
  },

  // Step 3 — Help & Support (Lenny + purple "?" buttons)
  3: {
    icon: <Question size={24} weight="duotone" />,
    title: "עזרה ותמיכה — תמיד כאן בשבילך",
    subtitle: "שני האזורים המסומנים הם הדרך הכי מהירה לקבל תשובה",
    body: (
      <>
        <div className="wo-settings-list wo-support-channels">
          <div className="wo-settings-item">
            <div className="wo-settings-icon wo-lenny-icon">
              <img src={lennyIcon} alt="" />
            </div>
            <div className="wo-settings-text">
              <span className="wo-settings-title">לני — העוזרת החכמה שלך</span>
              <span className="wo-settings-desc">
                הדמות המחייכת בצד המסך היא לני. לחיצה עליה פותחת צ&apos;אט שבו
                אפשר לשאול <strong>כל שאלה על השימוש במערכת</strong> — בעברית או
                באנגלית — ולקבל תשובה מיידית, בכל שעה.
              </span>
            </div>
          </div>

          <div className="wo-settings-item">
            <div className="wo-settings-icon wo-question-icon">
              <Question size={20} weight="bold" />
            </div>
            <div className="wo-settings-text">
              <span className="wo-settings-title">
                סימני השאלה{" "}
                <span className="wo-help-inline">
                  <Question size={12} weight="bold" />
                </span>{" "}
                הסגולים
              </span>
              <span className="wo-settings-desc">
                מפוזרים ליד כותרות ופיצ&apos;רים ברחבי המערכת. לחיצה עליהם פותחת{" "}
                <strong>הסבר קצר וממוקד</strong> על מה שנמצא לידם, ומשם אפשר גם
                לשלוח שאלה ישירות לצוות התמיכה.
              </span>
            </div>
          </div>
        </div>

        <p className="wo-support-tip">
          <CheckCircle
            size={15}
            weight="fill"
            style={{ color: "var(--color-primary)", verticalAlign: "middle", marginLeft: 4 }}
          />
          לא בטוחים איפה משהו נמצא או איך הוא עובד? <strong>תשאלו את לני</strong>.
          רוצים להבין פיצ&apos;ר מסוים במסך שבו אתם נמצאים? <strong>לחצו על סימן
          השאלה</strong> שלידו.
        </p>

        <div className="wo-support-callout">
          <Headset size={22} weight="duotone" />
          <span>
            ואם צריך בן אדם — כפתור <strong>&quot;תמיכה&quot;</strong> בחלק העליון
            של המסך פתוח לכל שאלה, בעיה או הצעה.
          </span>
        </div>

        <div className="wo-availability">
          לני זמינה <strong>24/7</strong>, וגם אנחנו תמיד נשמח לעזור.
          <br />
          אל תהססו לפנות — אנחנו כאן בשביל זה!
        </div>
      </>
    ),
  },
};

export default WelcomeOnboarding;

export function triggerWelcomeOnboarding() {
  window.dispatchEvent(new CustomEvent("clinikit:start-onboarding"));
}
